import {Box} from '@mui/material';
import {IUserDTO, UserDTO, UserRole} from 'api/api';
import {useDialog} from 'providers/dialog-provider';
import React, {FC, useCallback, useMemo} from 'react';
import {useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {selectNonAgentUsers, useDeleteUserMutation} from 'state/features/api/user-slice';
import {selectPageSettingByKey} from 'state/features/page-header-setting/page-header-setting-slice';
import {routes} from 'state/routes';
import {defaultVirtualizedListTableHeight} from 'styles/styling-constants';
import {ActionConfig, ColumnDef} from 'view/components/default-components/list-table/list-table-types';
import {VirtualizedListTable} from 'view/components/default-components/list-table/virtual-list-table';
import {localized, localizedDynamic, localizedInterpolation} from '../../../../i18n/i18n';
import {filterListItems, formatLastSeenDate} from '../../../../utilities/helpers/general-helpers';
import {UserResendInvitationAction} from './user-resend-invitation-action';

const columns: ColumnDef<IUserDTO>[] = [
  {field: 'name', header: localized('Name')},
  {field: 'role', header: localized('UserRole'), valueFormatter: (user) => localizedDynamic(UserRole[user.role])},
  {field: 'email', header: localized('Email')},
  {
    field: 'lastSeenApp',
    header: localized('LastSeenInApp'),
    valueFormatter: (user) => formatLastSeenDate(user.authId, user.lastSeenApp),
  },
  {
    field: 'lastSeenWeb',
    header: localized('LastSeenInWeb'),
    valueFormatter: (user) => formatLastSeenDate(user.authId, user.lastSeenWeb),
  },
];

export const UserManagementListView: FC = React.memo(() => {
  const users = useSelector(selectNonAgentUsers);
  const navigate = useNavigate();
  const pageSetting = useSelector(selectPageSettingByKey('userManagement'));
  const [deleteUser, {isLoading}] = useDeleteUserMutation();
  const {openDeleteDialog} = useDialog();

  const onEditClick = useCallback((user: IUserDTO) => navigate(`${routes.userManagement}/${user.id}/edit`), [navigate]);
  const onDeleteClick = useCallback(
    (user: IUserDTO) => {
      if (isLoading) return;
      openDeleteDialog(localizedInterpolation('ConfirmDeleteInt', 'User'), () => deleteUser(user as UserDTO));
    },
    [deleteUser, isLoading, openDeleteDialog],
  );

  const actions: ActionConfig<IUserDTO>[] = useMemo(
    () => [
      {
        icon: (user) => <UserResendInvitationAction user={user} />,
        label: localized('ResendInvitation'),
      },
    ],
    [],
  );

  const items = useMemo(() => filterListItems(users, ['name'], pageSetting?.searchString), [users, pageSetting]);

  return (
    <Box>
      <VirtualizedListTable<IUserDTO>
        height={defaultVirtualizedListTableHeight}
        rows={items}
        columns={columns}
        defaultOrderBy={'name'}
        actionHeader={localized('Edit')}
        onDelete={onDeleteClick}
        onEdit={onEditClick}
        actions={actions}
      />
    </Box>
  );
});
